// @flow
import type { AssetType } from './types';

export type ImageOptionsType = {
  // valid color
  fill?: string,
  focal?: string,
  format?: 'webp' | 'jpeg' | 'png' | false,
  isFitIn?: boolean,
  // 0 - 100
  quality?: number,
  size?: {
    height?: number,
    width?: number
  }
};

type OptionsBreakpointType = {
  breakpoint?: string,
  options?: ImageOptionsType
};

export type PropsType = {
  alt?: string,
  className?: string,
  options?: ImageOptionsType | OptionsBreakpointType[],
  src: string | AssetType
};

// from https://stackoverflow.com/a/54631141/1413689
let globalUseWebP = false;
if (typeof window !== 'undefined') {
  const img = new window.Image();
  img.onload = () => {
    globalUseWebP = img.width > 0 && img.height > 0;
  };
  img.src =
    'data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==';
}

function isNonTransformImage(srcParam?: string | AssetType) {
  let src: string = (srcParam: any) || '';
  if (srcParam && (srcParam: any).filename) {
    src = (srcParam: any).filename;
  }
  // the first condition here is for documentation, the CMS shouldn't allow external images
  return src.indexOf('storyblok.com') === -1 || src.match(/\.svg(\?|$)/gi);
}

function forceSslOnImageSrc(srcParam: string) {
  return srcParam.includes('//')
    ? `https://${srcParam.split('//')[1]}`
    : srcParam;
}

export function imageTransform(
  imageParam: ?string | AssetType,
  options: ImageOptionsType = {},
  useWebP?: boolean = globalUseWebP
) {
  let image: string = (imageParam: any);
  const assetImage: AssetType = (imageParam: any);
  if (assetImage?.filename) {
    image = assetImage.filename;
  }
  if (!image) return '';
  image = forceSslOnImageSrc(image);
  if (isNonTransformImage(image)) {
    return image;
  }
  const optionParams = [];
  if (options.isFitIn) {
    optionParams.push('fit-in');
  }
  if (options.size) {
    optionParams.push(
      `${options.size.width || 0}x${options.size.height || 0}${
        !options.focal && !assetImage?.focus ? '/smart' : ''
      }`
    );
  }
  const filters = [];
  if (options.format && options.format !== 'webp') {
    filters.push(`format(${options.format})`);
  } else if (options.format !== false && useWebP) {
    filters.push('format(webp)');
  }
  if (options.quality) {
    filters.push(`quality(${options.quality})`);
  }
  if (options.fill) {
    filters.push(`fill(${options.fill})`);
  }
  if (assetImage?.focus || options.focal) {
    const focalPoint = assetImage?.focus || options.focal;
    filters.push(`focal(${focalPoint || ''})`);
  }
  if (filters.length) {
    optionParams.push(`filters:${filters.join(':')}`);
  }
  if (!optionParams.length) return image;
  const imageService = 'https://img2.storyblok.com/';
  const path = image.replace(/(https?:)?\/\/a\.storyblok\.com/gi, '');
  return imageService + optionParams.join('/') + path;
}

const Image = ({ src, alt, options: optionsParam, className }: PropsType) => {
  if (!src) return null;
  let sources: OptionsBreakpointType[] = (optionsParam: any) || [];
  if (!optionsParam || !optionsParam.length) {
    sources = [{ options: (optionsParam: any) || {} }];
  }
  const defaultSource = sources.find(({ breakpoint }) => !breakpoint) || {};
  defaultSource.options = defaultSource.options || {};
  let filepath: string = (src: any) || '';
  const asset: AssetType = (src: any);
  if (asset?.filename) {
    filepath = asset.filename;
  }
  const altText =
    alt || asset?.alt || filepath.substring(filepath.lastIndexOf('/') + 1);
  const imageElement = (
    <img
      className={className}
      src={imageTransform(src, {
        ...defaultSource.options,
        format:
          defaultSource.options.format === 'webp'
            ? false
            : defaultSource.options.format
      })}
      alt={altText}
    />
  );
  if (isNonTransformImage(src)) {
    return imageElement;
  }
  return (
    <picture className={className}>
      {sources.map(({ breakpoint, options: breakpointOptions = {} }) => (
        <source
          key={breakpoint || 'default'}
          media={breakpoint}
          srcSet={imageTransform(
            src,
            { ...breakpointOptions, format: 'webp' },
            true
          )}
          type="image/webp"
        />
      ))}
      {imageElement}
    </picture>
  );
};

export default Image;
